/*@flow*/
import type { Node } from 'react';
import React, { useEffect, useState, createRef } from 'react';
import { Fragment } from 'react';
import { Hint, RadialChart, DiscreteColorLegend } from 'react-vis';
import numberFormat from 'underscore.string/numberFormat';
import omit from 'ramda/src/omit';
import keys from 'ramda/src/keys';
import compose from 'ramda/src/compose';
import map from 'ramda/src/map';
import sum from 'ramda/src/sum';
import filter from 'ramda/src/filter';
import createPortal from 'lib/createPortal';
import { getNonZeroKeys } from './utils';

type Str2StrT = { [key: string]: string };
type PieDataT = $Exact<{ type: string }>[];

type PropsT = {
  show?: boolean,
  data: PieDataT,
  headers: Str2StrT,
  suffixes: Str2StrT,
  colors: Str2StrT,
  width: number,
  activeHeader: string,
};

// TODO: make this smarter
const roundValue = (value: number) => {
  const thresh = 1000;
  if (Math.abs(value) < thresh) {
    return value;
  }
  let units = ['K+', 'M+', 'B+'];
  let u = -1;
  do {
    value /= thresh;
    ++u;
  } while (Math.abs(value) >= thresh && u < units.length - 1);
  return (
    <span>
      {value.toFixed(0)}
      <span className="small">{units[u]}</span>
    </span>
  );
};

const TotalPortal = (props: {
  container: HTMLElement | null,
  value: number,
}) => {
  const el = props.container;
  const [, setRandom] = useState();

  useEffect(() => {
    if (!el) return;
    const pieChartRef = $(el).find('> .pie-chart');
    let totalCountWrapper = pieChartRef.find('.total-count-wrapper').get(0);
    if (!totalCountWrapper) {
      pieChartRef.append('<div class="total-count-wrapper" />');
      setRandom(Math.random());
    }
  }, [el]);

  if (!el) return null;

  const node = $(el)
    .find('.total-count-wrapper')
    .get(0);

  return createPortal(
    <div className="total-count">{roundValue(props.value)}</div>,
    node
  );
};

// HACK: This should be handled in the backend. Create a default value for
// it.
const replaceEmptyStrWithNone = (maybeStr: ?string) => {
  let str = maybeStr;
  if (maybeStr && maybeStr.trim() === '') {
    str = 'none';
  }
  return str;
};

const makeHintBody = (headers, suffixes, value) =>
  compose(
    map((k) => {
      return (
        <p key={k}>
          {numberFormat(value[k])} {suffixes[k]}
        </p>
      );
    }),
    filter((k) => headers[k]),
    keys,
    omit(['label'])
  )(value);

const Dropdown = ({
  headers,
  onSelect,
  headerFilter,
  activeHeader,
}: {
  headers: Str2StrT,
  onSelect: (v: string) => void,
  activeHeader: string,
  headerFilter: (a: Str2StrT) => string[],
}) => {
  if (headerFilter(headers).length < 2) {
    return null;
  }
  const makeOptions = compose(
    map((k) => (
      <option key={k} value={k}>
        {headers[k]}
      </option>
    )),
    headerFilter
  );

  return (
    <select
      defaultValue={activeHeader}
      onChange={(v) => {
        onSelect(v.target.value);
      }}
    >
      {makeOptions(headers)}
    </select>
  );
};

const totalCount = compose(
  sum,
  map((v) => v.angle)
);

const getRadialData = (
  data: PieDataT,
  activeAnchor: string,
  colors: Str2StrT
) =>
  data
    .map((v) => {
      const label = replaceEmptyStrWithNone(v.type) || 'none';
      return {
        label,
        angle: v[activeAnchor],
        ...v,
        color: colors[label],
      };
    })
    .filter((v) => v.angle && v.angle !== 0);

const Legend = (props: {
  width: number,
  colors: Str2StrT,
  radialData: { label: string, angle: number }[],
}) => {
  const { width, colors, radialData } = props;

  const items = radialData
    .map((v) => v.label)
    .sort()
    .map((v) => ({ title: v, color: colors[v] }));

  // TODO: make this smarter
  const orientation =
    items.length > 4 && width < 400 ? 'vertical' : 'horizontal';

  return (
    <DiscreteColorLegend
      orientation={orientation}
      width={width}
      items={items}
    />
  );
};

export default (props: PropsT): null | Node => {
  const [value, setV] = useState();
  const [anchor, onSelect] = useState();
  const elRef = createRef();
  const { show, data, headers, suffixes, width, colors, activeHeader } = props;
  const [container, setContainer] = useState(null);

  useEffect(() => {
    setContainer(elRef.current);
  }, [elRef.current]);

  if (!show) {
    return null;
  }

  // filterNonZero:
  //
  // [
  //   {units: 0, sq_ft: 100},
  //   {units: 0, sq_ft: 200}
  // ]
  //
  // =>
  //
  // ['sq_ft']

  const activeAnchor = anchor || activeHeader;
  const radialData = getRadialData(data, activeAnchor, colors);

  return (
    <Fragment>
      <div className="pie-wrapper" onMouseLeave={() => setV()} ref={elRef}>
        <Dropdown
          headers={headers}
          onSelect={(el) => onSelect(el)}
          headerFilter={getNonZeroKeys(radialData)}
          activeHeader={activeHeader}
        />
        <RadialChart
          onValueMouseOver={(v) => setV(v)}
          onValueClick={(v) => setV(v)}
          data={radialData}
          className="pie-chart"
          innerRadius={80}
          radius={100}
          width={width}
          height={250}
          animation
          colorType="literal"
        >
          {value && (
            <Hint
              value={value}
              orientation="topright"
              style={{ background: 'black', color: 'white', padding: 10 }}
            >
              <div>
                <h4 style={{ margin: 0 }}>{value.label}</h4>
                {makeHintBody(headers, suffixes, value)}
              </div>
            </Hint>
          )}
        </RadialChart>
        <Legend colors={colors} radialData={radialData} width={width} />
      </div>
      <TotalPortal value={totalCount(radialData) || 0} container={container} />
    </Fragment>
  );
};
